import {useEffect, useRef, useState} from 'react'
import {api} from '../../lib/api.js'
import {useSessionGeneration} from '../../lib/useSessionGeneration.js'
import {createSchoolTeamController} from '../../lib/schoolTeamState.js'
import styles from './repVolume.module.css'

export default function SchoolTeamPicker({school, conference, season, expectedVersion, actorId = '', members = [], defaultOpen = false, onSaved}) {
  const [open,setOpen] = useState(defaultOpen)
  const [catalog,setCatalog] = useState(null)
  const [selected,setSelected] = useState([])
  const [reason,setReason] = useState('')
  const [state,setState] = useState({status:'idle',msg:''})
  const generation = useSessionGeneration()
  const controller = useRef(null), saved = useRef(onSaved)
  saved.current = onSaved
  const input = {school,conference,season,expectedVersion}
  useEffect(() => {
    const next = createSchoolTeamController({actorId,send:body => api.fbsBowlDeskPick(body),read:() => api.fbsBowlDeskPicks(),onState:setState,onSaved:r => saved.current?.(r)})
    controller.current = next
    setCatalog(null); setSelected([]); setReason('')
    setState(next.hasPending({school,season}) ? {status:'uncertain',msg:'A previous save needs reconciliation.'} : {status:'idle',msg:''})
    return () => {next.dispose(); controller.current = null}
  },[school,conference,season,expectedVersion,generation,actorId])
  useEffect(() => {
    if (!open) return undefined
    const ctl = new AbortController()
    api.fbsBowlDeskPicks({signal:ctl.signal}).then(data => {
      if (ctl.signal.aborted) return
      setCatalog(data)
      setSelected(members.map(m => m.id))
    }).catch(error => {if (!ctl.signal.aborted) setState({status:'error',msg:error.message || 'Could not read active school-team members.'})})
    return () => ctl.abort()
  },[open,school,season,expectedVersion,generation]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setOpen(defaultOpen) }, [defaultOpen])
  const candidates = catalog?.schoolTeamCandidates || []
  const ready = catalog?.schoolTeamVersion === 1 && catalog?.canPick === true
    && catalog?.deskMeta?.version === expectedVersion && (catalog?.season ?? catalog?.deskMeta?.season) === season
  const writesEnabled = catalog?.schoolTeamWritesEnabled === true
  const staffWritesEnabled = catalog?.staffTeamVersion === 2 && catalog?.staffTeamWritesEnabled === true
  const selectedStaffBlocked = !staffWritesEnabled && selected.some(id => candidates.find(c => c.id === id)?.identityKind === 'staff')
  const missingPartners = candidates.filter(c => Object.hasOwn(c, 'pairedWithId') && (!c.pairedWithId || !candidates.some(p => p.id === c.pairedWithId)))
  const invalidSelection = selected.some(id => !candidates.some(c => c.id === id) || missingPartners.some(c => c.id === id))
  const blocked = !ready || !writesEnabled || ['saving','uncertain'].includes(state.status)
  const toggle = (candidate, checked) => {
    const next = new Set(selected)
    if (checked) {next.add(candidate.id); if (candidate.pairedWithId) next.add(candidate.pairedWithId)}
    else {next.delete(candidate.id); if (candidate.pairedWithId) next.delete(candidate.pairedWithId)}
    setSelected([...next])
  }
  const submit = event => {
    event.preventDefault()
    if (blocked || selectedStaffBlocked || invalidSelection) return
    controller.current?.submit({...input,members:selected,reason},candidates,{staffTeamWritesEnabled:staffWritesEnabled})
  }
  return <details open={open} className={styles.pickBox} onToggle={event => setOpen(event.currentTarget.open)}>
    <summary className={styles.deskAction}>{members.length ? 'Manage team' : 'Assign team'}</summary>
    {open && <form className={styles.pickForm} onSubmit={submit} aria-label={`School team for ${school}`}>
      <p className={styles.deskWhy}>Choose every sales desk and additional participant for {school}. Standing support stays with its desk. This records bowl coverage; it does not change the school’s CRM owner or choose a primary.</p>
      {!catalog && <p role="status">Reading active school-team members…</p>}
      {catalog && !ready && <p role="status">School-team editing is unavailable or the desk version changed. Refresh the board before saving.</p>}
      {catalog && !writesEnabled && <p role="status">School-team editing is disabled. {catalog.schoolTeamWriteDisabledReason || 'New team changes are not enabled on the server.'} Existing team coverage remains visible.</p>}
      {ready && missingPartners.length > 0 && <p role="status">Standing support is unavailable for {missingPartners.map(c => c.requiredPairName ? `${c.requiredPairName} (support for ${c.name})` : c.name).join(', ')}. Those desks cannot be saved without their verified support partner.</p>}
      {ready && candidates.some(c => c.identityKind === 'staff') && !staffWritesEnabled && <p role="status">Staff-member editing is disabled pending the compatible release and approved enrollment. Existing coverage and saved-history recovery remain visible.</p>}
      {ready && <fieldset className={styles.teamChoices} disabled={blocked}>
        <legend>People on this school</legend>
        {candidates.map(candidate => {
          const requiredPair = candidates.some(c => c.pairedWithId === candidate.id && selected.includes(c.id))
          const needsStaff = candidate.identityKind === 'staff' || candidates.find(c => c.id === candidate.pairedWithId)?.identityKind === 'staff'
          const unavailable = missingPartners.some(c => c.id === candidate.id) || (!staffWritesEnabled && needsStaff && !selected.includes(candidate.id))
          return <label key={candidate.id} className={styles.teamChoice}>
            <input type="checkbox" checked={selected.includes(candidate.id)} disabled={requiredPair || unavailable}
              onChange={event => toggle(candidate,event.target.checked)} />
            {candidate.name} ({candidate.role}){candidate.identityKind === 'staff' ? ' — staff, no login' : ''}{requiredPair ? ' — standing support' : ''}
          </label>
        })}
      </fieldset>}
      <label className={styles.pickLabel}>Reason
        <input value={reason} disabled={blocked} maxLength={300} required onChange={event => setReason(event.target.value)} />
      </label>
      <button className="btn" type="submit" disabled={blocked || selectedStaffBlocked || invalidSelection}>Save school team</button>
      {members.length > 0 && <button className="btn" type="button" disabled={blocked} onClick={() => controller.current?.submit({...input,members:[],reason,action:'clear'},candidates)}>Clear this school’s team</button>}
      {state.status === 'uncertain' && <button className="btn" type="button" onClick={() => controller.current?.reconcile(input)}>Read saved school-team history</button>}
      {state.msg && <p role="status" className={state.status === 'error' || state.status === 'uncertain' ? styles.deskBad : styles.deskWhy}>{state.msg}</p>}
    </form>}
  </details>
}
